<template>
  <main class="home-page">
    <!-- Hero -->
    <section class="hero">
      <div class="hero-content">
        <div class="hero-badge">{{ $t('home.heroBadge') }}</div>
        <h1 class="hero-title">
          <span class="title-accent">{{ $t('home.heroTitleAccent') }}</span>
          <span>{{ $t('home.heroTitle') }}</span>
        </h1>
        <p class="hero-desc">{{ $t('home.heroDesc') }}</p>
        <div class="hero-actions">
          <button v-if="!isLoggedIn" class="btn-primary" @click="openLogin">{{ $t('home.registerNow') }}</button>
          <button class="btn-outline" @click="goFlashOptions">{{ $t('home.startTrading') }}</button>
        </div>
      </div>
      <div class="hero-visual">
        <div class="hero-stats-grid">
          <div class="stat-card">
            <div class="stat-icon">⚡</div>
            <div class="stat-value">60s</div>
            <div class="stat-label">{{ $t('home.fastSettlement') }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">🌱</div>
            <div class="stat-value">10</div>
            <div class="stat-label">{{ $t('home.energyTypes') }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">📊</div>
            <div class="stat-value">50%</div>
            <div class="stat-label">{{ $t('home.maxReturn') }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-icon">🔒</div>
            <div class="stat-value">24/7</div>
            <div class="stat-label">{{ $t('home.allDayTrading') }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 交易品種 -->
    <section class="section-card">
      <div class="section-head">
        <h3>{{ $t('home.popularAssets') }}</h3>
        <p>{{ $t('home.popularAssetsDesc') }}</p>
      </div>
      <div class="asset-grid">
        <div class="asset-card" v-for="asset in assets" :key="asset.symbol" @click="goTrade(asset.symbol)">
          <div class="asset-icon-wrap" :style="{ background: asset.bg }">
            <span class="asset-emoji">{{ asset.icon }}</span>
          </div>
          <div class="asset-info">
            <h4>{{ asset.name }}</h4>
            <p>{{ asset.desc }}</p>
          </div>
          <div class="asset-tag">{{ $t('home.flashOptionTag') }}</div>
        </div>
      </div>
    </section>

    <!-- 平臺優勢 -->
    <section class="section-card">
      <div class="section-head">
        <h3>{{ $t('home.whyChooseUs') }}</h3>
        <p>{{ $t('home.whyChooseUsDesc') }}</p>
      </div>
      <div class="advantage-grid">
        <div class="advantage-card" v-for="(item, idx) in advantages" :key="idx">
          <div class="adv-icon">{{ item.icon }}</div>
          <h4>{{ item.title }}</h4>
          <p>{{ item.desc }}</p>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="section-card">
      <div class="section-head">
        <h3>{{ $t('home.faq') }}</h3>
      </div>
      <div class="faq-list">
        <div class="faq-item" v-for="(faq, idx) in faqs" :key="idx">
          <button class="faq-q" @click="toggleFaq(idx)">
            <span>{{ faq.q }}</span>
            <span class="faq-arrow" :class="{ open: faq.open }">›</span>
          </button>
          <div v-if="faq.open" class="faq-a">{{ faq.a }}</div>
        </div>
      </div>
    </section>

    <!-- 底部 CTA -->
    <section class="cta-section">
      <h3>{{ $t('home.ctaTitle') }}</h3>
      <p>{{ $t('home.ctaDesc') }}</p>
      <button v-if="!isLoggedIn" class="btn-primary" @click="openLogin">{{ $t('home.registerFree') }}</button>
      <button v-else class="btn-primary" @click="goFlashOptions">{{ $t('home.goTrading') }}</button>
    </section>
  </main>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import userStore from '@/utils/userStore'

export default {
  name: 'Home',
  setup() {
    const router = useRouter()
    const { t, tm, rt } = useI18n()
    const isLoggedIn = ref(false)

    const openLogin = () => {
      window.dispatchEvent(new CustomEvent('open-login-modal'))
    }

    const goFlashOptions = () => {
      router.push('/flash-options')
    }

    const goTrade = (symbol) => {
      router.push({ path: '/flash-options', query: { symbol } })
    }

    const iconMap = {
      BTC: '☀️', ETH: '💧', BNB: '🌬️', SOL: '⚡', XRP: '🌿',
      DOGE: '🔋', ADA: '🌾', AVAX: '🔥', DOT: '⚛️', LINK: '🛢️'
    }
    const bgMap = {
      BTC: 'linear-gradient(135deg, #fff7e6, #ffe8cc)', ETH: 'linear-gradient(135deg, #e6fffb, #b5f5ec)',
      BNB: 'linear-gradient(135deg, #e6f7ff, #bae7ff)', SOL: 'linear-gradient(135deg, #fff1f0, #ffd8bf)',
      XRP: 'linear-gradient(135deg, #f6ffed, #d9f7be)', DOGE: 'linear-gradient(135deg, #f0f5ff, #d6e4ff)',
      ADA: 'linear-gradient(135deg, #fffbe6, #fff1b8)', AVAX: 'linear-gradient(135deg, #fff2e8, #ffd8bf)',
      DOT: 'linear-gradient(135deg, #f0f5ff, #d6e4ff)', LINK: 'linear-gradient(135deg, #f9f0ff, #efdbff)'
    }

    const rawAssets = ref([])

    const assets = computed(() => rawAssets.value.map(a => ({
      symbol: a.symbol,
      icon: iconMap[a.symbol] || '⚡',
      name: t(`flashOptions.asset_${a.symbol}`, a.name || a.symbol),
      desc: t(`home.assetDesc.${a.symbol}`, a.name || a.symbol),
      bg: bgMap[a.symbol] || 'linear-gradient(135deg, #f6ffed, #d9f7be)'
    })))

    async function loadAssets () {
      try {
        const res = await fetch('/api/c/flash-options/config')
        const json = await res.json()
        if (json.success && json.data?.assets) {
          rawAssets.value = json.data.assets.map(a => ({ symbol: a.symbol, name: a.name }))
        }
      } catch (e) {
        console.warn('loadAssets failed:', e)
      }
    }

    const advantages = computed(() => {
      const list = tm('home.advantages') || []
      return list.map(item => ({
        icon: rt(item.icon),
        title: rt(item.title),
        desc: rt(item.desc)
      }))
    })

    const faqOpens = ref([false, false, false])
    const faqs = computed(() => {
      const list = tm('home.faqs') || []
      return list.map((item, idx) => ({
        q: rt(item.q),
        a: rt(item.a),
        open: faqOpens.value[idx] || false
      }))
    })
    const toggleFaq = (idx) => {
      faqOpens.value[idx] = !faqOpens.value[idx]
    }

    onMounted(() => {
      isLoggedIn.value = userStore.isLoggedIn()
      window.addEventListener('user-connected', () => {
        isLoggedIn.value = userStore.isLoggedIn()
      })
      loadAssets()
    })

    return { isLoggedIn, openLogin, goFlashOptions, goTrade, assets, advantages, faqs, toggleFaq }
  }
}
</script>

<style scoped>
.home-page {
  min-height: 100vh;
  padding: 76px 16px 100px;
  background: #f0f2f5;
  color: #1a2233;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Hero */
.hero {
  background: linear-gradient(135deg, #e8f5e9 0%, #e0f2f1 50%, #e3f2fd 100%);
  border-radius: 20px;
  padding: 28px 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.hero-badge {
  display: inline-block;
  background: linear-gradient(135deg, #43a047, #00897b);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 14px;
  border-radius: 20px;
  width: fit-content;
  letter-spacing: 0.5px;
}

.hero-title {
  margin: 0;
  font-size: 28px;
  line-height: 1.2;
  font-weight: 800;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.title-accent {
  color: #2e7d32;
}

.hero-desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
  color: #546e7a;
}

.hero-actions {
  display: flex;
  gap: 12px;
}

.btn-primary {
  background: linear-gradient(135deg, #43a047, #2e7d32);
  color: #fff;
  border: none;
  border-radius: 12px;
  height: 44px;
  padding: 0 28px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 14px rgba(46, 125, 50, 0.3);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(46, 125, 50, 0.4);
}

.btn-outline {
  background: #fff;
  color: #2e7d32;
  border: 2px solid #2e7d32;
  border-radius: 12px;
  height: 44px;
  padding: 0 24px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-outline:hover {
  background: #f1f8e9;
}

.hero-stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 16px;
  text-align: center;
  border: 1px solid rgba(46, 125, 50, 0.1);
}

.stat-icon {
  font-size: 24px;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 22px;
  font-weight: 800;
  color: #2e7d32;
}

.stat-label {
  font-size: 12px;
  color: #78909c;
  margin-top: 2px;
  font-weight: 600;
}

/* Section Card */
.section-card {
  background: #fff;
  border-radius: 20px;
  padding: 24px 20px;
  border: 1px solid #dce1e8;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.section-head h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #1a2233;
}

.section-head p {
  margin: 6px 0 0;
  font-size: 13px;
  color: #78909c;
}

/* Asset Grid */
.asset-grid {
  margin-top: 16px;
  display: grid;
  gap: 12px;
}

.asset-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid #dce1e8;
  background: #f7f8fa;
  transition: all 0.2s;
  cursor: pointer;
}

.asset-card:hover {
  border-color: #a5d6a7;
  box-shadow: 0 4px 16px rgba(46, 125, 50, 0.08);
}

.asset-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.asset-emoji {
  font-size: 22px;
}

.asset-info {
  flex: 1;
  min-width: 0;
}

.asset-info h4 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #1a2233;
}

.asset-info p {
  margin: 2px 0 0;
  font-size: 12px;
  color: #90a4ae;
}

.asset-tag {
  font-size: 11px;
  font-weight: 700;
  color: #2e7d32;
  background: #e8f5e9;
  padding: 4px 10px;
  border-radius: 8px;
  white-space: nowrap;
}

/* Advantages */
.advantage-grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.advantage-card {
  padding: 18px 14px;
  border-radius: 16px;
  border: 1px solid #dce1e8;
  background: #f7f8fa;
  text-align: center;
  transition: all 0.2s;
}

.advantage-card:hover {
  border-color: #a5d6a7;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(46, 125, 50, 0.06);
}

.adv-icon {
  font-size: 28px;
  margin-bottom: 8px;
}

.advantage-card h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #1a2233;
}

.advantage-card p {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.6;
  color: #78909c;
}

/* FAQ */
.faq-list {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.faq-item {
  border-radius: 14px;
  border: 1px solid #dce1e8;
  background: #f7f8fa;
  overflow: hidden;
}

.faq-q {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 16px 18px;
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 700;
  color: #1a2233;
  cursor: pointer;
  text-align: left;
}

.faq-arrow {
  font-size: 20px;
  color: #2e7d32;
  transition: transform 0.25s;
  font-weight: 700;
}

.faq-arrow.open {
  transform: rotate(90deg);
}

.faq-a {
  padding: 0 18px 16px;
  font-size: 13px;
  line-height: 1.7;
  color: #78909c;
}

/* CTA */
.cta-section {
  background: linear-gradient(135deg, #2e7d32, #00897b);
  border-radius: 20px;
  padding: 32px 24px;
  text-align: center;
  color: #fff;
}

.cta-section h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
}

.cta-section p {
  margin: 8px 0 20px;
  font-size: 14px;
  opacity: 0.88;
}

.cta-section .btn-primary {
  background: #fff;
  color: #2e7d32;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.cta-section .btn-primary:hover {
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.2);
}

/* Desktop */
@media (min-width: 769px) {
  .home-page {
    padding: 28px 32px 40px;
    gap: 24px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .hero {
    flex-direction: row;
    align-items: center;
    padding: 48px;
    gap: 48px;
  }

  .hero-content {
    flex: 1;
  }

  .hero-visual {
    flex: 0 0 380px;
  }

  .hero-title {
    font-size: 36px;
  }

  .hero-desc {
    font-size: 16px;
    max-width: 520px;
  }

  .asset-grid {
    grid-template-columns: 1fr 1fr;
  }

  .advantage-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  .faq-list {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
}
</style>
